// frontend/src/components/company/CompanyForm.tsx

import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Button } from '@/components/common/UI/Button'
import { Input } from '@/components/common/UI/Input'
import { useToast } from '@/components/common/UI/Toast'

// ============================================
// Схема валидации
// ============================================

const companySchema = z.object({
  name: z.string().min(2, 'Название компании должно содержать минимум 2 символа').max(255),
  description: z.string().max(5000, 'Описание не должно превышать 5000 символов').optional(),
  website: z.string().url('Введите корректный URL').optional().or(z.literal('')),
  industry: z.string().max(100).optional(),
  employees_count: z.number().min(0).optional().or(z.null()),
  founded_year: z.number().min(1900).max(new Date().getFullYear()).optional().or(z.null()),
  phone: z.string().regex(/^\+?[0-9]{10,15}$/, 'Введите корректный номер телефона').optional().or(z.literal('')),
  email: z.string().email('Введите корректный email').optional().or(z.literal('')),
  address: z.string().max(500).optional(),
  inn: z.string().regex(/^\d{10}$|^\d{12}$/, 'ИНН должен содержать 10 или 12 цифр').optional().or(z.literal('')),
  kpp: z.string().regex(/^\d{9}$/, 'КПП должен содержать 9 цифр').optional().or(z.literal('')),
  ogrn: z.string().regex(/^\d{13}$/, 'ОГРН должен содержать 13 цифр').optional().or(z.literal('')),
  legal_address: z.string().max(500).optional(),
  benefits_text: z.string().optional(),
  social_links_text: z.string().optional(),
})

type CompanyFormData = z.infer<typeof companySchema>

// ============================================
// Компонент формы
// ============================================

interface CompanyFormProps {
  company?: any
  onSuccess?: () => void
  onCancel?: () => void
}

export const CompanyForm: React.FC<CompanyFormProps> = ({
  company,
  onSuccess,
  onCancel
}) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [isVerifying, setIsVerifying] = useState(false)
  const [innVerified, setInnVerified] = useState<boolean | null>(null)
  const [companyInfo, setCompanyInfo] = useState<any>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)

  useEffect(() => {
    if (!logoFile) { setLogoPreview(null); return }
    const preview = URL.createObjectURL(logoFile)
    setLogoPreview(preview)
    return () => URL.revokeObjectURL(preview)
  }, [logoFile])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue,
    watch
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      name: company?.name || '',
      description: company?.description || '',
      website: company?.website || '',
      industry: company?.industry || '',
      employees_count: company?.employees_count || null,
      founded_year: company?.founded_year || null,
      phone: company?.phone || '',
      email: company?.email || '',
      address: company?.address || '',
      inn: company?.inn || '',
      kpp: company?.kpp || '',
      ogrn: company?.ogrn || '',
      legal_address: company?.legal_address || '',
      benefits_text: (company?.benefits || []).join('\n'),
      social_links_text: Object.entries(company?.social_links || {}).map(([name, url]) => `${name}: ${url}`).join('\n'),
    }
  })

  const inn = watch('inn')

  // Проверка ИНН
  const verifyINN = async () => {
    if (!inn || inn.length < 10) {
      showToast('Введите ИНН для проверки', 'warning')
      return
    }

    setIsVerifying(true)
    try {
      const response = await apiClient.post('/v1/verification/inn/precheck', { inn })
      setInnVerified(response.is_valid)
      setCompanyInfo(response.company_info)
      
      if (response.is_valid) {
        showToast('ИНН подтвержден', 'success')
        // Автозаполнение полей
        if (response.company_info) {
          const info = response.company_info
          if (info.full_name && !company) {
            setValue('name', info.full_name)
          }
          if (info.legal_address) {
            setValue('address', info.legal_address)
          }
        }
      } else {
        showToast('ИНН недействителен или компания не найдена', 'error')
      }
    } catch (error) {
      showToast('Ошибка проверки ИНН', 'error')
    } finally {
      setIsVerifying(false)
    }
  }

  // Мутация для создания/обновления компании
  const mutation = useMutation({
    mutationFn: async (data: CompanyFormData) => {
      const payload = Object.fromEntries(
        Object.entries(data).filter(([key]) => !key.endsWith('_text')).map(([key, value]) => [key, typeof value === 'string' && !value.trim() ? null : value]),
      ) as Record<string, unknown>
      payload['benefits'] = data.benefits_text?.split('\n').map(value => value.trim()).filter(Boolean) || []
      const socialLinks: Record<string, string> = {}
      for (const line of (data.social_links_text || '').split('\n')) {
        const separator = line.indexOf(':')
        if (separator < 1) continue
        const name = line.slice(0, separator).trim()
        const rawUrl = line.slice(separator + 1).trim()
        if (name && rawUrl) socialLinks[name] = rawUrl.startsWith('http') ? rawUrl : `https://${rawUrl}`
      }
      payload['social_links'] = socialLinks
      payload['employees_count'] = data.employees_count ? Number(data.employees_count) : null
      payload['founded_year'] = data.founded_year ? Number(data.founded_year) : null

      const saved = company
        ? await apiClient.put(`/v1/companies/${company.id}`, payload)
        : await apiClient.post('/v1/companies', payload)
      if (logoFile && saved?.id) await apiClient.upload(`/v1/companies/${saved.id}/logo`, logoFile)
      return saved
    },
    onSuccess: (saved: any) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] })
      if (company) {
        queryClient.invalidateQueries({ queryKey: ['company', company.id] })
      }
      if (saved?.id) queryClient.invalidateQueries({ queryKey: ['company', saved.id] })
      showToast(
        company ? 'Компания обновлена' : 'Компания создана',
        'success'
      )
      if (onSuccess) onSuccess()
    },
    onError: (error: any) => {
      showToast(
        error.response?.data?.detail || error.message || 'Произошла ошибка',
        'error'
      )
    }
  })

  const onSubmit = (data: CompanyFormData) => {
    // Если ИНН указан, но не проверен
    if (data.inn && data.inn !== company?.inn && innVerified !== true) {
      showToast('Пожалуйста, проверьте ИНН перед сохранением', 'warning')
      return
    }

    mutation.mutate(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Основная информация */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Input
            label="Название компании *"
            placeholder="ООО Ромашка"
            error={errors.name?.message}
            {...register('name')}
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-secondary-700">
            Описание
            <textarea
              placeholder="Расскажите о компании..."
              rows={4}
              aria-invalid={Boolean(errors.description)}
              className="mt-1.5 w-full rounded-lg border border-secondary-300 bg-white px-3 py-2.5 text-secondary-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              {...register('description')}
            />
            {errors.description?.message && <span className="mt-1 block text-sm text-error">{errors.description.message}</span>}
          </label>
        </div>

        <Input
          label="Сайт"
          placeholder="https://example.com"
          error={errors.website?.message}
          {...register('website')}
        />

        <Input
          label="Индустрия"
          placeholder="IT, Финансы, Ритейл..."
          error={errors.industry?.message}
          {...register('industry')}
        />

        <Input
          label="Количество сотрудников"
          type="number"
          placeholder="100"
          error={errors.employees_count?.message}
          {...register('employees_count', { setValueAs: (value) => value === '' ? null : Number(value) })}
        />

        <Input
          label="Год основания"
          type="number"
          placeholder="2020"
          error={errors.founded_year?.message}
          {...register('founded_year', { setValueAs: (value) => value === '' ? null : Number(value) })}
        />
      </div>

      {/* Контакты */}
      <div className="border-t border-secondary-200 pt-4">
        <h3 className="mb-3 text-sm font-medium text-secondary-700">Логотип компании</h3>
        <div className="flex items-center gap-4">{(logoPreview || company?.logo_url) && <img src={logoPreview || company.logo_url} alt="Предпросмотр логотипа" className="h-16 w-16 rounded-lg border object-contain" />}<input type="file" accept="image/jpeg,image/png,image/webp" onChange={event => { const selected = event.target.files?.[0]; if (selected && selected.size > 5 * 1024 * 1024) { showToast('Логотип должен быть не больше 5 МБ', 'warning'); event.target.value = ''; return }; setLogoFile(selected || null) }} aria-label="Загрузить логотип" /></div>
        <p className="mt-1 text-xs text-secondary-500">Можно выбрать JPG, PNG или WebP до 5 МБ. Для новой компании логотип загрузится после создания.</p>
      </div>

      {/* Контакты */}
      <div className="border-t border-secondary-200 pt-4">
        <h3 className="text-sm font-medium text-secondary-700 mb-4">Контактная информация</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Телефон"
            placeholder="+7 999 123-45-67"
            error={errors.phone?.message}
            {...register('phone')}
          />

          <Input
            label="Email"
            type="email"
            placeholder="info@company.com"
            error={errors.email?.message}
            {...register('email')}
          />

          <div className="md:col-span-2">
            <Input
              label="Адрес"
              placeholder="г. Москва, ул. Тверская, д. 1"
              error={errors.address?.message}
              {...register('address')}
            />
          </div>
        </div>
      </div>

      {/* Юридическая информация */}
      <div className="border-t border-secondary-200 pt-4">
        <h3 className="text-sm font-medium text-secondary-700 mb-4">Юридическая информация</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="ИНН"
              placeholder="7707083893"
              error={errors.inn?.message}
              {...register('inn')}
            />
            {inn && inn.length >= 10 && (
              <button
                type="button"
                onClick={verifyINN}
                disabled={isVerifying}
                className="mt-1 text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
              >
                {isVerifying ? 'Проверка...' : 'Проверить ИНН'}
              </button>
            )}
            {innVerified !== null && (
              <p className={`mt-1 text-sm ${innVerified ? 'text-success' : 'text-error'}`}>
                {innVerified ? '✅ ИНН подтвержден' : '❌ ИНН недействителен'}
              </p>
            )}
            {companyInfo && (
              <div className="mt-2 p-3 bg-secondary-50 rounded-lg text-sm space-y-1">
                <p><span className="text-secondary-500">Название:</span> {companyInfo.full_name}</p>
                <p><span className="text-secondary-500">Статус:</span> {companyInfo.status}</p>
                <p><span className="text-secondary-500">Директор:</span> {companyInfo.director}</p>
              </div>
            )}
          </div>
          <Input label="КПП" placeholder="770701001" error={errors.kpp?.message} {...register('kpp')} />
          <Input label="ОГРН" placeholder="1027700132195" error={errors.ogrn?.message} {...register('ogrn')} />
          <div className="md:col-span-2"><Input label="Юридический адрес" placeholder="Адрес по данным ЕГРЮЛ" error={errors.legal_address?.message} {...register('legal_address')} /></div>
        </div>
      </div>

      <div className="border-t border-secondary-200 pt-4 space-y-4">
        <h3 className="text-sm font-medium text-secondary-700">Преимущества и соцсети</h3>
        <label className="block text-sm font-medium text-secondary-700">Что предлагает компания<textarea rows={3} placeholder="По одному преимуществу на строку" className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2" {...register('benefits_text')} /></label>
        <label className="block text-sm font-medium text-secondary-700">Ссылки на соцсети и страницы компании<textarea rows={3} placeholder={'Например:\nVK: https://vk.com/company\nTelegram: https://t.me/company'} className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2" {...register('social_links_text')} /></label>
      </div>

      {/* Кнопки */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-secondary-200">
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Отмена
          </Button>
        )}
        <Button type="submit" isLoading={isSubmitting || mutation.isPending}>
          {company ? 'Сохранить' : 'Создать компанию'}
        </Button>
      </div>
    </form>
  )
}
